import type { SSEEvent, SSEEventType } from '../types'

export type SSEEventHandler = (event: SSEEvent) => void

const SSE_DEBUG = false
const sseLog = (...args: any[]) => {
  if (SSE_DEBUG) console.log(...args)
}
const sseWarn = (...args: any[]) => {
  if (SSE_DEBUG) console.warn(...args)
}

export class SSEClient {
  private eventSource: EventSource | null = null
  private currentJobId: string | null = null
  private handlers: Map<SSEEventType, Set<SSEEventHandler>> = new Map()
  private allHandlers: Set<SSEEventHandler> = new Set()

  connect(jobId: string): void {
    // Don't reconnect if already connected to the same job
    if (this.eventSource && this.currentJobId === jobId) {
      sseLog('[SSE] Already connected to job:', jobId)
      return
    }

    this.disconnect()
    this.currentJobId = jobId

    const url = `/api/stream/${jobId}`
    sseLog('[SSE] Connecting to:', url)

    this.eventSource = new EventSource(url)

    this.eventSource.onopen = () => {
      sseLog('[SSE] Connection opened for job:', jobId)
      // Dispatch a custom event to signal connection is ready
      window.dispatchEvent(new CustomEvent('sse_connected', { detail: { jobId } }))
    }

    this.eventSource.onerror = (err) => {
      sseWarn('[SSE] Error:', err)
      // EventSource will automatically reconnect on error
    }

    this.eventSource.onmessage = (e) => {
      try {
        const rawEvent: any = JSON.parse(e.data)
        // Normalize event type (backend uses event_type, frontend expects type)
        const event: SSEEvent = {
          ...rawEvent,
          type: rawEvent.type || rawEvent.event_type,
        }
        const eventType = event.type || event.event_type || 'error'
        sseLog('[SSE] Event received:', eventType, event)

        // Call type-specific handlers
        const typeHandlers = this.handlers.get(eventType)
        if (typeHandlers) {
          typeHandlers.forEach((handler) => handler(event))
        }

        // Call all handlers
        this.allHandlers.forEach((handler) => handler(event))
      } catch (error) {
        sseWarn('[SSE] Failed to parse event:', error, e.data)
      }
    }
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      this.currentJobId = null
    }
  }

  on(type: SSEEventType, handler: SSEEventHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set())
    }
    this.handlers.get(type)!.add(handler)

    // Return unsubscribe function
    return () => {
      this.handlers.get(type)?.delete(handler)
    }
  }

  onAny(handler: SSEEventHandler): () => void {
    this.allHandlers.add(handler)
    return () => {
      this.allHandlers.delete(handler)
    }
  }

  off(type: SSEEventType, handler: SSEEventHandler): void {
    this.handlers.get(type)?.delete(handler)
  }

  offAny(handler: SSEEventHandler): void {
    this.allHandlers.delete(handler)
  }
}

// Singleton instance
export const sseClient = new SSEClient()

